<template>
  <form
    autocomplete="off"
    class="service-creation service-creation__form"
    v-on:submit.prevent="onSubmit">
    <textarea-length-limitation
      title="Название"
      v-bind:max-length="255"
      v-bind:violations="nameErrors"
      is-auto-height-expanding
      v-letter-capital
      v-model="name"
    />
    <input-text
      title="Код номенклатуры"
      v-model="code"
      v-bind:violations="codeErrors"
    />
    <input-text
      title="Код клиники"
      v-bind:allowed-null="true"
      v-bind:without-native-controls="true"
      v-bind:violations="clinicCodeErrors"
      v-bind:maxlength="10"
      v-model="clinicCode"
    />
    <dl class="service-creation__form__category">
      <dt>Прайс: </dt>
      <v-autocomplete
        class="service-creation__form__category__name"
        :menu-props="{ maxWidth: 586 }"
        required="required"
        hide-details
        item-text="name"
        item-value="id"
        v-bind:items="initPriceList"
        v-model="priceId">
        <template v-slot:no-data>
          <p style="padding: 10px; margin:0;">Результатов нет</p>
        </template>
      </v-autocomplete>
    </dl>
    <p
      class="service-creation__form__error-text"
      v-for="(error, index) in priceListErrors"
      v-bind:key="`price-list-index-${index}`">
      {{ error }}
    </p>
    <dl class="service-creation__form__category">
      <dt>Категория:</dt>
      <v-autocomplete
        class="service-creation__form__category__name"
        :menu-props="{ maxWidth: 586 }"
        required="required"
        hide-details
        item-text="name"
        item-value="id"
        v-bind:items="categoriesList"
        v-model="categoryId">
        <template v-slot:no-data>
          <p style="padding: 10px; margin:0;">Результатов нет</p>
        </template>
      </v-autocomplete>
    </dl>
    <p
      class="service-creation__form__error-text"
      v-for="(error, index) in categoryErrors"
      v-bind:key="index">
      {{ error }}
    </p>
    <div class="service-creation__form__row">
      <input-text
        required="required"
        title="Стоимость в рублях"
        v-bind:violations="priceErrors"
        v-mask="priceMask"
        v-model="price"
      />
      <the-select
        title="Признак расчета"
        v-bind:options="$options.itemTypeOptions"
        v-bind:violations="itemTypeErrors"
        v-model="itemType"
      />
    </div>
    <div
      class="service-creation__form__row-taxcode"
      v-if="!itemTypeGoods">
      <the-select
        title="Код услуги"
        v-bind:options="serviceTaxCodeOptions"
        v-bind:violations="taxCodeErrors"
        v-bind:value="taxCode"
        v-on:input="onInputTaxCode"
      />
      <div
        v-if="isSelectedCategoryId"
      >
        <button
          class="service-creation__form__button-copy"
          type="button"
          v-on:click="onSetTaxCodeForAllServicesInCategory"
        />
        Применить ко всем услугам раздела
      </div>
    </div>
    <div class="service-creation__form__buttons">
      <button
        class="service-creation__form__button-cancel"
        type="button"
        v-on:click.prevent="close">
        Отмена
      </button>
      <button
        type="submit"
        class="service-creation__form__button-save">
        Сохранить
      </button>
    </div>
  </form>
</template>

<script>
import InputText from '@/components/InputText.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TheSelect from '@/components/TheSelect.vue';
import { unitOptions } from '@/mixins/pricesMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';

/**
 * @description Название события отправки формы.
 * @type {string}
 */
const NAME_EVENT_SUBMIT = 'form-submit';

/**
 * @description Название события установки кода услуги для всех услуг категории.
 * @type {string}
 */
const NAME_EVENT_SET_TAX_CODE_FOR_ALL_SERVICES_IN_CATEGORY = 'set-tax-code-for-all-services-in-category';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);
/**
 * @description Форма создания/редактирования услуги.
 * @type {any}
 */
export default {
  name: 'FormServiceCreation',
  components: {
    TheSelect,
    InputText,
    TextareaLengthLimitation,
  },
  /**
   * @description Опции признак расчета.
   * @type {any[]}
   */
  itemTypeOptions: [
    {
      value: 'service',
      title: 'Услуга',
    },
    {
      value: 'goods',
      title: 'Товар',
    },
  ],
  /**
   * @description Опции НДС.
   * @type {any[]}
   */
  vatOptions: [
    {
      value: null,
      title: 'По умолчанию',
    },
    {
      value: 'none',
      title: 'НДС не облагается',
    },
    {
      value: 'vat0',
      title: 'НДС чека по ставке 0%',
    },
    {
      value: 'vat10',
      title: 'НДС чека по ставке 10%',
    },
    {
      value: 'vat110',
      title: 'НДС чека по расчетной ставке 10/110%',
    },
    {
      value: 'vat20',
      title: 'НДС чека по ставке 20%',
    },
    {
      value: 'vat120',
      title: 'НДС чека по расчетной ставке 20/120%',
    },
  ],
  computed: {
    categoryErrors: resolveInputErrors('service.category'),
    colorErrors: resolveInputErrors('service.color'),
    itemTypeErrors: resolveInputErrors('service.item-type'),
    nameErrors: resolveInputErrors('service.name'),
    clinicCodeErrors: resolveInputErrors('service.clinic_code'),
    codeErrors: resolveInputErrors('service.code'),
    priceErrors: resolveInputErrors('service.price'),
    priceListErrors: resolveInputErrors('service.price_list_id'),
    taxCodeErrors: resolveInputErrors('service.tax-code'),
    unitsErrors: resolveInputErrors('service.units'),
    /**
     * @description Список категорий.
     * @type {{id:number,name:string}[]}
     */
    categoriesList() {
      return this.initPriceList?.find(
        (item) => item.id === this.priceId,
      )?.serviceCategories;
    },
    /**
     * @description Категория выбрана.
     * @returns {string}
     */
    isSelectedCategoryId() {
      return this.categoryId !== '';
    },
    /**
     * @description Выбран Признак расчета "Товар".
     * @returns {boolean}
     */
    itemTypeGoods() {
      return this.itemType === 'goods';
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      name: vm.name,
      code: vm.code,
      clinicCode: vm.clinicCode,
      categoryId: vm.categoryId,
      priceId: vm.priceId,
      price: vm.price,
      itemType: vm.itemType,
      taxCode: vm.taxCode,
    }),
  },
  data() {
    return {
      /**
       * Доступные единицы измерения для услуги
       * @type {array}
       */
      unitOptions,
      /**
       * @description Идентификатор категории.
       * @type {string}
       */
      categoryId: this.initialCategoryId ? this.initialCategoryId : this.currentCategoryId,
      /**
       * @description Идентификатор прайса.
       * @type {string}
       */
      priceId: this.initialPriceId,
      /**
       * @description Цвет.
       * @type {string}
       */
      color: this.initialColor,
      /**
       * @description Список доступных цветов.
       * @type {string[]}
       */
      colors: [
        '#DAD9FC',
        '#F7D9FC',
        '#FFD1D1',
        '#FCE1D9',
        '#F8EECC',
        '#F2F8CC',
        '#D2EDB0',
        '#D0FDD1',
        '#D0FDF0',
        '#D0F5FD',
      ],
      /**
       * @description Название.
       * @type {string}
       */
      name: this.initialName,
      /**
       * @description Цена.
       * @type {string}
       */
      price: this.initialPrice.toString(),
      /**
       * @description Код услуги для налоговой.
       * @type {?string}
       */
      taxCode: this.initialTaxCode,
      /**
       * @description Ед. измерения.
       * @type {string}
       */
      units: this.initialUnits,
      /**
       * @description Признак расчета.
       * @type {string}
       */
      itemType: this.initialItemType,
      /**
       * @description Код клиники.
       * @type {string}
       */
      clinicCode: this.initialClinicCode ?? '',
      /**
       * @description Код номенклатуры.
       * @type {string}
       */
      code: this.initialCode ?? '',
    };
  },
  methods: {
    /**
     * @description Закрыть форму.
     * @returns {void}
     */
    close() {
      this.$emit('close');
    },
    /**
     * @description Маска ввода стоимости.
     * @param {string} value Значение.
     * @returns {string[]}
     */
    priceMask(value) {
      const regex = /[^\d.]/gmu;
      const price = value.replace(regex, '');
      const format = /^[\d]{0,10}/g;
      const result = format.exec(price);
      if (result !== null) {
        return [result[0]];
      }
      return [price];
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const props = {
        categoryId: this.categoryId,
        code: this.code,
        color: this.color,
        itemType: this.itemType,
        name: this.name,
        price: Number(this.price),
        taxCode: this.taxCode,
        units: this.units,
        clinicCode: this.clinicCode,
        priceListId: this.priceId,
        order: this.initialOrder,
      };
      this.$emit(NAME_EVENT_SUBMIT, props);
    },
    /**
     * @description Обработать установку кода услуги для всех услуг категории.
     * @returns {void}
     */
    onSetTaxCodeForAllServicesInCategory() {
      this.$emit(
        NAME_EVENT_SET_TAX_CODE_FOR_ALL_SERVICES_IN_CATEGORY,
        this.categoryId,
        this.taxCode,
      );
    },
    /**
     * @description Обработать ввод кода услуги для налоговой.
     * @param {string} taxCode
     * @returns {void}
     */
    onInputTaxCode(taxCode) {
      this.taxCode = taxCode || null;
    },
  },
  mixins: [formMeaningfulFieldsMixin],
  props: {
    /**
     * @description Начальное значение для порядкового номера услуги.
     * @type {Number}
     */
    initialOrder: {
      required: false,
      default: () => 0,
      type: Number,
    },
    /**
     * @description Список папок,
     * @type {any[]}
     */
    initPriceList: {
      required: true,
      type: Array,
    },
    /**
     * @description Категории без прайс листа
     * @type {Array}
     */
    categoriesWithoutPrice: {
      required: true,
      type: Array,
    },
    /**
     * @description Код номенклатуры.
     */
    initialCode: {
      default: () => null,
      required: false,
      type: String,
    },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: {
      required: true,
      type: Object,
    },
    currentCategoryId: { type: [Number, String], default: () => '' },
    currentPriceId: { type: [Number, String], default: () => '' },
    initialCategoryId: { type: [Number, String], default: () => '' },
    initialPriceId: { type: [Number, String], default: () => '' },
    initialColor: { type: String, default: () => '' },
    initialItemType: { type: String, default: () => '' },
    initialName: { type: String, default: () => '' },
    initialPrice: { type: [String, Number], default: () => '' },
    initialTaxCode: { type: String, default: null },
    initialUnits: { type: String, default: () => '' },
    initialVat: { type: String, default: () => '' },
    serviceTaxCodeOptions: { required: true, type: Array },
    initialClinicCode: { type: String, default: null },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.service-creation {
  position: relative;
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 15px;
    margin: 49px 0 0 0;
    align-content: flex-end;
    &__duration {
      display: flex;
      flex-direction: row;
      gap: 10px;
      &__label {
        font-weight: 400;
        color: #192a3e;
        font-size: 16px;
        line-height: 19px;
      }
    }
    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      align-items: start;
    }
    &__row-taxcode {
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 10px;
      align-items: center;
    }
    &__group {
      display: flex;
      flex-direction: column;
      gap: 10px;
      &.field_error {
        .service-creation__form__label {
          color: var(--warning-color);
        }
      }
    }
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90a0b7;
      margin: 0 0 2px 0;
    }
    &__category {
      display: flex;
      flex-direction: row;
      grid-gap: 15px;
      font-weight: 400;
      color: #192a3e;
      font-size: 16px;
      line-height: 19px;
      align-items: flex-end;
      width: fit-content();
      justify-items: center;
      dt {
        margin-bottom: 7px;
      }
      &__name {
        font-weight: 600;
        max-width: auto;
        min-width: 250px;
        flex: 0;
        margin: 0;
        padding: 0;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 10px;
      max-width: 438px;
      width: 100%;
      margin: 30px 0 0 auto;
    }
    &__button {
      &-cancel {
        @include buttons.btn-transparent(100%, 56px, 20px);
      }
      &-copy {
        align-self: flex-end;
        margin-top: 28px;
        background-image: url("@/assets/img/copy-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        width: 24px;
        height: 24px;
        padding: 0;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/copy-blue.svg");
        }
      }
      &-save {
        @include buttons.btn-blue(100%, 56px, 20px);
        &:disabled {
          background-color: var(--unfocus-color);
        }
      }
    }
    &__icon {
      &__caption {
        font-weight: 600;
        font-size: 16px;
        line-height: 19px;
        color: #192a3e;
      }
      &__colors {
        flex: 1;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
      }
      &__color {
        min-width: 36px;
        width: 36px;
        height: 36px;
        min-height: 36px;
        border-radius: 50%;
        margin: 0 10px 10px 0;
        cursor: pointer;
        transition: border-width 0.1s;
        &.outline {
          border: 4px solid #c2cfe0;
        }
        &:hover {
          border: 4px solid #c2cfe0;
        }
      }
    }
    &__error-text {
      color: var(--warning-color);
      font-size: 12px;
      line-height: 12px;
      padding: 0;
      margin: 0;
    }
  }
  &__exit {
    position: absolute;
    right: -15px;
    top: 0px;
    background-image: url("@/assets/img/exit-black.svg");
    background-position: center;
    background-size: auto;
    background-repeat: no-repeat;
    background-color: #eaeff3;
    padding: 9px;
    border-radius: 2px;
    width: 32px;
    height: 32px;
    cursor: pointer;

    &:hover {
      background-color: transparent;
    }
  }
}

.service-creation-code {
  font-size: .75rem;
  & &__term,
  & &__value {
    display: inline;
  }
}
</style>
